import React, { useCallback } from 'react';
import { FormikValues } from 'formik';
import { TextField, TextFieldProps } from '@material-ui/core';
import {
    AREA_CODES,
    AreaCodeField,
    ECountryCode,
} from './components/AreaCodeSelect';
import PhoneMask from 'src/utils/masks/NumberFormatPhone';
import { removeCountryCodeFromPhone } from 'src/modules/phones/utils/removeCountryCodeFromPhone';

interface IAreaCodeTextField {
    name: string;
    countryCodeName?: string;
    values: FormikValues;
    setFieldValue: Function;
    touched: any;
    errors: any;
    handleChange: Function;
    props?: TextFieldProps;
}

const getCountryCode = (countryCode?: ECountryCode) => {
    return countryCode && AREA_CODES[countryCode]
        ? countryCode
        : ECountryCode.Mexico;
};

const AreaCodeTextFieldComponent: React.FC<IAreaCodeTextField> = ({
    values,
    name,
    countryCodeName = 'countryCode',
    touched,
    errors,
    setFieldValue,
    handleChange,
    props,
}) => {
    const selectedCountryCode = getCountryCode(values?.[countryCodeName]);
    const value = values?.[name] || '';
    const error = touched?.[name] && errors?.[name];

    const handleCountryCodeChange = useCallback(
        (countryCode: ECountryCode) => {
            setFieldValue(countryCodeName, countryCode);
            setFieldValue(name, removeCountryCodeFromPhone(value));
        },
        [name, countryCodeName, setFieldValue, value]
    );

    return (
        <TextField
            fullWidth
            name={name}
            required
            value={value}
            onChange={(e) => {
                handleChange(e);
            }}
            error={Boolean(error)}
            helperText={error ? errors[name] : ''}
            InputProps={{
                inputComponent: PhoneMask,
                inputProps: {
                    format: AREA_CODES[selectedCountryCode].format,
                },
                startAdornment: (
                    <AreaCodeField
                        countryCode={selectedCountryCode}
                        setCountryCode={handleCountryCodeChange}
                    />
                ),
            }}
            {...props}
        />
    );
};

export const AreaCodeTextField = React.memo(
    AreaCodeTextFieldComponent,
    (prevProps, nextProps) => {
        const prevName = prevProps.name;
        const nextName = nextProps.name;

        return (
            prevName === nextName &&
            prevProps.values?.[prevName] === nextProps.values?.[nextName] &&
            prevProps.values?.countryCode === nextProps.values?.countryCode &&
            prevProps.touched?.[prevName] === nextProps.touched?.[nextName] &&
            prevProps.errors?.[prevName] === nextProps.errors?.[nextName] &&
            prevProps.props === nextProps.props
        );
    }
);
